import { alpha, createTheme, type Theme } from "@mui/material/styles";

export const appThemeTokens = {
  colors: {
    primary: "#23233a",
    secondary: "#b7c4d6",
    background: "#f6f8fb",
    paper: "#ffffff",
    textStrong: "#1f2937",
    textMuted: "#667085",
    accent: "#7aa2c7",
    surface: "#f9fbff",
    surfaceBorder: "#dbe5f0",
  },
  radii: {
    sm: 12,
    md: 18,
    lg: 24,
    pill: 999,
  },
  shadows: {
    soft: "0 16px 36px rgba(15, 23, 42, 0.08)",
    nav: "0 14px 30px rgba(15, 23, 42, 0.08)",
  },
} as const;

export const appTheme = createTheme({
  palette: {
    primary: {
      main: appThemeTokens.colors.primary,
      contrastText: "#fff",
    },
    secondary: {
      main: appThemeTokens.colors.secondary,
    },
    background: {
      default: appThemeTokens.colors.background,
      paper: appThemeTokens.colors.paper,
    },
  },
  shape: {
    borderRadius: appThemeTokens.radii.sm,
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h2: {
      color: appThemeTokens.colors.textStrong,
      fontWeight: 700,
      borderBottom: `2px solid ${appThemeTokens.colors.secondary}`,
      paddingBottom: "0.3rem",
      marginBottom: "1rem",
      fontSize: "2rem",
    },
    h3: {
      color: appThemeTokens.colors.primary,
      fontWeight: 700,
      fontSize: "1.5rem",
      marginBottom: "0.5rem",
    },
    button: {
      fontWeight: 700,
      letterSpacing: "0.01em",
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: `${appThemeTokens.radii.pill}px`,
          paddingInline: "1rem",
          paddingBlock: "0.55rem",
          fontWeight: 700,
          letterSpacing: "0.01em",
          textTransform: "none",
          transition:
            "background-color 180ms ease, color 180ms ease, border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        text: {
          color: appThemeTokens.colors.textStrong,
          "&:hover": {
            backgroundColor: alpha(appThemeTokens.colors.accent, 0.10),
          },
        },
        outlined: {
          borderColor: alpha(appThemeTokens.colors.textStrong, 0.12),
          backgroundColor: alpha("#ffffff", 0.76),
          color: appThemeTokens.colors.textStrong,
          "&:hover": {
            borderColor: alpha(appThemeTokens.colors.accent, 0.32),
            backgroundColor: alpha(appThemeTokens.colors.accent, 0.08),
          },
        },
        contained: {
          backgroundColor: appThemeTokens.colors.textStrong,
          color: "#ffffff",
          boxShadow: `0 12px 24px ${alpha("#c9d8e7", 0.22)}`,
          "&:hover": {
            backgroundColor: "#111827",
            boxShadow: `0 14px 28px ${alpha("#c9d8e7", 0.26)}`,
          },
        },
        containedInherit: {
          backgroundColor: alpha("#ffffff", 0.86),
          color: appThemeTokens.colors.textStrong,
          "&:hover": {
            backgroundColor: "#ffffff",
          },
        },
      },
    },
  },
});

export const getFooterStyles = (theme: Theme) => ({
  root: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    py: 1,
    backgroundColor: alpha(appThemeTokens.colors.surface, 0.95),
    color: appThemeTokens.colors.textStrong,
    borderTop: `1px solid ${appThemeTokens.colors.surfaceBorder}`,
    boxShadow: "0 -12px 24px rgba(15, 23, 42, 0.06)",
    backdropFilter: "blur(18px)",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    maxWidth: "100vw",
    overflow: "hidden",
  },
  mobileGroup: {
    display: "flex",
    gap: 1,
    p: 0.4,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    backgroundColor: alpha("#ffffff", 0.84),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: `0 10px 22px ${alpha("#c9d8e7", 0.18)}`,
  },
  desktopLinks: {
    display: "flex",
    gap: 1,
    p: 0.4,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    backgroundColor: alpha("#ffffff", 0.88),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: `0 10px 22px ${alpha("#c9d8e7", 0.18)}`,
  },
  socialLinks: {
    display: "flex",
    gap: 1,
    ml: 1.5,
    p: 0.35,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    backgroundColor: alpha("#ffffff", 0.76),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
  },
  iconButton: {
    minWidth: 0,
    p: 1,
    color: appThemeTokens.colors.textStrong,
  },
  linkButton: {
    minWidth: 0,
    px: 1.4,
    py: 0.8,
    color: appThemeTokens.colors.textMuted,
    "&:hover": {
      color: appThemeTokens.colors.textStrong,
    },
  },
  menuPaper: {
    mt: 1,
    borderRadius: `${appThemeTokens.radii.md}px`,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: appThemeTokens.shadows.soft,
    backgroundColor: alpha("#ffffff", 0.96),
    backdropFilter: "blur(16px)",
  },
  menuItem: {
    minWidth: 220,
    py: 1.1,
    px: 1.5,
    fontWeight: 600,
    color: appThemeTokens.colors.textStrong,
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.10),
      color: theme.palette.primary.main,
    },
  },
}) as const;

export const getTeamCardStyles = (theme: Theme) => ({
  card: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    borderRadius: `${appThemeTokens.radii.lg}px`,
    backgroundColor: alpha("#ffffff", 0.92),
    color: appThemeTokens.colors.textStrong,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    boxShadow: appThemeTokens.shadows.soft,
    backdropFilter: "blur(14px)",
    transition:
      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 22px 38px ${alpha("#c9d8e7", 0.22)}`,
      borderColor: alpha(appThemeTokens.colors.accent, 0.20),
    },
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
  },
  title: {
    fontWeight: 800,
    mb: 1,
    color: appThemeTokens.colors.textStrong,
  },
  bodyText: {
    mb: 1,
    color: appThemeTokens.colors.textMuted,
    lineHeight: 1.55,
    "& strong": {
      color: appThemeTokens.colors.textStrong,
    },
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
    transition: "color 180ms ease, border-color 180ms ease",
    "&:hover": {
      color: appThemeTokens.colors.accent,
      borderColor: alpha(appThemeTokens.colors.accent, 0.40),
    },
  },
  socialRow: {
    display: "flex",
    gap: 1,
    p: 2,
    pt: 0,
    alignItems: "center",
  },
  socialLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    color: appThemeTokens.colors.textStrong,
    backgroundColor: alpha(appThemeTokens.colors.accent, 0.10),
    border: `1px solid ${alpha(appThemeTokens.colors.accent, 0.14)}`,
    transition:
      "background-color 180ms ease, color 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.18),
      color: theme.palette.primary.main,
      transform: "translateY(-1px)",
    },
  },
}) as const;

export const getPageHeaderStyles = (_theme: Theme) => ({
  actions: {
    mb: 3,
    display: "flex",
    alignItems: "stretch",
    flexWrap: "wrap",
    gap: { xs: 1, sm: 1.25 },
    "& > *:nth-child(1)": {
      marginRight: "auto",
    },
  },
  actionButton: {
    px: { xs: 1.2, sm: 1.8 },
    py: { xs: 0.65, sm: 0.9 },
    minHeight: { xs: 38, sm: 42 },
    borderRadius: `${appThemeTokens.radii.sm}px`,
    fontSize: { xs: "0.75rem", sm: "0.9rem" },
    whiteSpace: "nowrap",
  },
  primaryAction: {
    backgroundColor: alpha(appThemeTokens.colors.accent, 0.16),
    color: appThemeTokens.colors.textStrong,
    border: `1px solid ${alpha(appThemeTokens.colors.accent, 0.26)}`,
    boxShadow: `0 10px 20px ${alpha("#c9d8e7", 0.18)}`,
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.24),
      borderColor: alpha(appThemeTokens.colors.accent, 0.34),
    },
  },
  secondaryAction: {
    backgroundColor: alpha("#ffffff", 0.88),
    color: appThemeTokens.colors.textStrong,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.08),
      borderColor: alpha(appThemeTokens.colors.accent, 0.22),
    },
  },
}) as const;

export const getAppSurfaceStyles = (theme: Theme) => ({
  pageCard: {
    borderRadius: `${appThemeTokens.radii.lg}px`,
    backgroundColor: alpha("#ffffff", 0.92),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    boxShadow: appThemeTokens.shadows.soft,
    backdropFilter: "blur(14px)",
  },
  pageContainer: {
    maxWidth: 900,
    mx: "auto",
    mt: 4,
    mb: 8,
    p: { xs: 2, sm: 4 },
    borderRadius: `${appThemeTokens.radii.lg}px`,
    backgroundColor: alpha("#ffffff", 0.92),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    boxShadow: appThemeTokens.shadows.soft,
    backdropFilter: "blur(14px)",
  },
  mapFrame: {
    width: "100%",
    overflow: "hidden",
    borderRadius: `${appThemeTokens.radii.md}px`,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: `0 14px 26px ${alpha("#c9d8e7", 0.18)}`,
    backgroundColor: alpha("#ffffff", 0.92),
  },
  sectionTitle: {
    mb: 1,
    fontWeight: 700,
    color: appThemeTokens.colors.textStrong,
  },
  sectionText: {
    mb: 0.75,
    color: appThemeTokens.colors.textMuted,
    lineHeight: 1.6,
    "& strong": {
      color: appThemeTokens.colors.textStrong,
    },
  },
  bodyText: {
    color: appThemeTokens.colors.textMuted,
    lineHeight: 1.6,
    "& strong": {
      color: appThemeTokens.colors.textStrong,
    },
  },
  bodyLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
    transition: "color 180ms ease, border-color 180ms ease",
    "&:hover": {
      color: appThemeTokens.colors.accent,
      borderColor: alpha(appThemeTokens.colors.accent, 0.36),
    },
  },
  socialLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 42,
    height: 42,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    color: appThemeTokens.colors.textStrong,
    backgroundColor: alpha(appThemeTokens.colors.accent, 0.10),
    border: `1px solid ${alpha(appThemeTokens.colors.accent, 0.16)}`,
    transition:
      "background-color 180ms ease, color 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.18),
      color: theme.palette.primary.main,
      transform: "translateY(-1px)",
    },
  },
  heroImage: {
    width: 120,
    height: 120,
    objectFit: "cover",
    borderRadius: "50%",
    border: `3px solid ${alpha("#ffffff", 0.94)}`,
    backgroundColor: "#ffffff",
    boxShadow: `0 16px 28px ${alpha("#c9d8e7", 0.25)}`,
    cursor: "pointer",
    transition: "transform 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 20px 34px ${alpha("#c9d8e7", 0.3)}`,
    },
  },
  dialogSurface: {
    p: 2,
    backgroundColor: alpha(appThemeTokens.colors.surface, 0.98),
  },
  dialogImage: {
    width: { xs: 340, sm: 520, md: 700, lg: 900 },
    maxWidth: "95vw",
    maxHeight: "85vh",
    objectFit: "contain",
    boxShadow: appThemeTokens.shadows.soft,
    backgroundColor: "#ffffff",
    display: "block",
    mx: "auto",
    border: "none",
    borderRadius: `${appThemeTokens.radii.md}px`,
  },
  dialogCaption: {
    color: appThemeTokens.colors.textStrong,
    fontWeight: 700,
  },
  calloutPanel: {
    mt: 4,
    mb: 5,
    p: 3,
    borderRadius: `${appThemeTokens.radii.lg}px`,
    backgroundColor: alpha("#ffffff", 0.9),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: appThemeTokens.shadows.soft,
  },
  pinLabel: {
    backgroundColor: alpha("#ffffff", 0.94),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    px: 1.5,
    py: 0.65,
    fontSize: 14,
    boxShadow: `0 10px 22px ${alpha("#c9d8e7", 0.2)}`,
    whiteSpace: "nowrap",
    color: appThemeTokens.colors.textStrong,
    transition: "background-color 180ms ease, color 180ms ease",
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.12),
      cursor: "pointer",
    },
  },
  accentHeading: {
    color: appThemeTokens.colors.textStrong,
    fontWeight: 700,
  },
  mutedHeading: {
    color: appThemeTokens.colors.textMuted,
    fontWeight: 700,
  },
}) as const;

export const getNavbarStyles = (theme: Theme) => ({
  appBar: {
    backgroundColor: alpha(appThemeTokens.colors.surface, 0.94),
    color: appThemeTokens.colors.textStrong,
    borderBottom: `1px solid ${appThemeTokens.colors.surfaceBorder}`,
    boxShadow: appThemeTokens.shadows.nav,
    backdropFilter: "blur(18px)",
  },
  toolbar: {
    minHeight: { xs: 72, md: 82 },
    gap: 2,
  },
  brand: {
    flexGrow: 1,
    width: "fit-content",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontSize: { xs: "0.95rem", sm: "1.05rem" },
    cursor: "pointer",
    color: appThemeTokens.colors.textStrong,
  },
  brandAccent: {
    display: "inline-flex",
    alignItems: "center",
    px: 1.5,
    py: 0.75,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    backgroundColor: alpha("#ffffff", 0.78),
    boxShadow: `0 8px 18px ${alpha("#c9d8e7", 0.25)}`,
    transition:
      "background-color 180ms ease, border-color 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: "#ffffff",
      borderColor: alpha(appThemeTokens.colors.accent, 0.34),
      transform: "translateY(-1px)",
    },
  },
  desktopNav: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    p: 0.75,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    backgroundColor: alpha("#ffffff", 0.86),
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: `0 10px 22px ${alpha("#c9d8e7", 0.22)}`,
  },
  navButton: {
    color: appThemeTokens.colors.textMuted,
    borderRadius: `${appThemeTokens.radii.pill}px`,
    px: 1.7,
    py: 1,
    fontSize: "0.98rem",
    textTransform: "none",
    transition:
      "background-color 180ms ease, color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.12),
      color: appThemeTokens.colors.textStrong,
      boxShadow: `inset 0 0 0 1px ${alpha(appThemeTokens.colors.accent, 0.18)}`,
      transform: "translateY(-1px)",
    },
  },
  menuIconButton: {
    color: appThemeTokens.colors.textStrong,
    borderRadius: `${appThemeTokens.radii.md}px`,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.08)}`,
    backgroundColor: alpha("#ffffff", 0.82),
    "&:hover": {
      backgroundColor: "#ffffff",
    },
  },
  menuPaper: {
    mt: 1.2,
    borderRadius: `${appThemeTokens.radii.md}px`,
    border: `1px solid ${alpha(appThemeTokens.colors.textStrong, 0.06)}`,
    boxShadow: appThemeTokens.shadows.soft,
    overflow: "hidden",
    backdropFilter: "blur(16px)",
    backgroundColor: alpha("#ffffff", 0.96),
  },
  menuItem: {
    minWidth: 220,
    py: 1.15,
    px: 1.5,
    fontWeight: 600,
    color: appThemeTokens.colors.textStrong,
    textTransform: "none",
    transition: "background-color 180ms ease, color 180ms ease",
    "&:hover": {
      backgroundColor: alpha(appThemeTokens.colors.accent, 0.10),
      color: theme.palette.primary.main,
    },
  },
}) as const;